/**
 * @file _course-eligibility.js
 * @description Renders personalised funding eligibility UI on the course single page.
 *
 * On init, snapshots each [data-role="funding-eligibility"] container's innerHTML
 * and indexes its [data-goal-target] list items by target value. On eligibility
 * events (or cross-tab storage changes), re-renders with eligible/ineligible tag
 * lists, or restores the original HTML when no data is present.
 *
 * @requires window.QLD.eligibilityStorage - EligibilityStorage singleton (from _eligibility-storage.js)
 * @requires window.QLD.fundingMatcher - FundingMatcher singleton (from _funding-matcher.js)
 *
 * Listens for:
 *   - eligibility:saved   — user completed the survey
 *   - eligibility:cleared — user data was cleared
 *   - storage             — cross-tab localStorage change
 */

(function () {
    'use strict';

    const ICON_BASE_CLASSES = 'fa-light qld__icon qld__icon--sm';

    const CLASS_NAMES = {
        LINK_LIST_FLEX: 'qld__link-list qld__link-list--flex',
        TAG_LIST_COMPACT: 'qld__tag-list qld__margin-t-none',
        TAG_FINANCIAL: 'qld__tag qld__tag--financial',
        TAG_INELIGIBLE: 'qld__tag--ineligible',
        TABLE_TIGHT: 'qld__table--tight',
        ICON_ELIGIBLE: `${ICON_BASE_CLASSES} fa-circle-check qld__icon--success`,
        ICON_INELIGIBLE: `${ICON_BASE_CLASSES} fa-circle-xmark qld__icon--unavailable`
    };

    const FundingEligibilityUI = {

        // Keyed by data-fe-id string. Each entry: { originalHTML, items: { [target]: liElement } }
        _store: new Map(),
        // Keyed by data-fe-cost-id string. Each entry: { originalHTML }
        _costStore: new Map(),
        _initialized: false,

        getContainers() {
            return document.querySelectorAll('[data-role="funding-eligibility"]');
        },

        getCostContainers() {
            return document.querySelectorAll('[data-role="funding-costs"]');
        },

        getTargets(container) {
            const stored = this._store.get(container.getAttribute('data-fe-id'));
            if (!stored) return [];
            return Object.keys(stored.items).map(target => ({
                target,
                label: stored.items[target].textContent.trim()
            }));
        },

        getSurveyUrl() {
            const storedData = window.QLD && window.QLD.eligibilityStorage && window.QLD.eligibilityStorage.get();
            const storedUrl = storedData && storedData.surveyUrl;
            let safeStoredUrl = null;

            try {
                if (storedUrl && /^https?:\/\//i.test(storedUrl)) {
                    const parsedUrl = new URL(storedUrl);
                    if (parsedUrl.origin === window.location.origin) {
                        safeStoredUrl = storedUrl;
                    }
                }
            } catch (error) {
                console.warn('Invalid stored survey URL in eligibility data:', error);
            }

            return safeStoredUrl || (window.NSWP && window.NSWP.urls && window.NSWP.urls.eligibilitySurvey);
        },

        restoreOriginal(container) {
            const stored = this._store.get(container.getAttribute('data-fe-id'));
            if (!stored) return;
            container.innerHTML = stored.originalHTML;
        },

        restoreCostOriginal(container) {
            const stored = this._costStore.get(container.getAttribute('data-fe-cost-id'));
            if (!stored) return;
            container.innerHTML = stored.originalHTML;
        },

        createIconListWithContent(iconClasses, fragments) {
            const ul = document.createElement('ul');
            ul.className = CLASS_NAMES.LINK_LIST_FLEX;
            const li = document.createElement('li');

            const icon = document.createElement('i');
            icon.className = iconClasses;
            icon.setAttribute('aria-hidden', 'true');
            li.appendChild(icon);

            const div = document.createElement('div');
            
            // Add all fragments in the order given
            fragments.forEach(fragment => {
                if (fragment) {
                    div.appendChild(fragment);
                }
            });
            
            li.appendChild(div);
            ul.appendChild(li);

            return ul;
        },

        buildFundingCostIntro() {
            const p = document.createElement('p');
            p.append(
                "How much you'll pay if you access ",
                Object.assign(document.createElement('strong'), { textContent: 'government funding' }),
                ' for this course:'
            );
            return p;
        },

        buildTagList(items, options) {
            const opts = options || {};
            const list = document.createElement('ul');
            list.className = CLASS_NAMES.TAG_LIST_COMPACT;

            (items || []).forEach(item => {
                const li = item.cloneNode(true);
                if (opts.ineligible) {
                    li.classList.add(CLASS_NAMES.TAG_INELIGIBLE);
                }
                list.appendChild(li);
            });

            return list;
        },

        buildCostRow(row) {
            const tr = document.createElement('tr');
            const tdProgram = document.createElement('td');
            const tag = document.createElement('span');
            tag.className = CLASS_NAMES.TAG_FINANCIAL;
            tag.textContent = row.label;
            tdProgram.appendChild(tag);

            const tdCost = document.createElement('td');
            tdCost.textContent = row.costWording || 'Cost information unavailable';

            tr.appendChild(tdProgram);
            tr.appendChild(tdCost);
            return tr;
        },

        getCostWording(target, stored) {
            const item = stored && stored.items && stored.items[target];
            return item ? item.getAttribute('data-cost-wording') || '' : '';
        },

        renderFundingCosts(matchesByTarget) {
            const costContainers = this.getCostContainers();
            if (!costContainers || costContainers.length === 0) return;

            const hasData = !!(window.QLD && window.QLD.eligibilityStorage && window.QLD.eligibilityStorage.hasData && window.QLD.eligibilityStorage.hasData());
            const rows = Object.values(matchesByTarget || {});

            costContainers.forEach(container => {
                if (!hasData) {
                    this.restoreCostOriginal(container);
                    return;
                }

                if (rows.length === 0) {
                    container.replaceChildren();
                    return;
                }

                const fragment = document.createDocumentFragment();
                fragment.appendChild(this.buildFundingCostIntro());

                const table = document.createElement('table');
                table.className = CLASS_NAMES.TABLE_TIGHT;

                const tbody = document.createElement('tbody');
                rows.forEach(row => {
                    tbody.appendChild(this.buildCostRow(row));
                });

                table.appendChild(tbody);
                fragment.appendChild(table);

                container.replaceChildren(fragment);
            });
        },

        renderMessage(container, result) {
            if (result.status === 'no_data' || (result.eligible.length === 0 && result.ineligible.length === 0)) {
                this.restoreOriginal(container);
                return;
            }

            const stored = this._store.get(container.getAttribute('data-fe-id'));
            if (!stored) return;

            const fragment = document.createDocumentFragment();

            const heading = document.createElement('p');
            const strong = document.createElement('strong');

            const surveyUrl = this.getSurveyUrl();

            const responsesEl = surveyUrl ? Object.assign(document.createElement('a'), { href: surveyUrl, textContent: 'your responses' }) : document.createTextNode('your responses');
            strong.append('Based on ', responsesEl);
            heading.appendChild(strong);
            fragment.appendChild(heading);

            if (result.eligible.length > 0) {
                const p = document.createElement('p');
                p.append(
                    'You may be ',
                    Object.assign(document.createElement('strong'), { textContent: 'eligible' }),
                    ' for this funding:'
                );

                const eligibleItems = result.eligible.map(({ target }) => stored.items[target]);
                const tagUl = this.buildTagList(eligibleItems);
                
                const fragments = [p, tagUl];
                if (stored.fundingTypeText) {
                    fragments.push(stored.fundingTypeText.cloneNode(true));
                }
                
                const heading = this.createIconListWithContent(
                    CLASS_NAMES.ICON_ELIGIBLE,
                    fragments
                );
                fragment.appendChild(heading);
            }

            if (result.ineligible.length > 0) {
                const p = document.createElement('p');
                p.append(
                    'You ',
                    Object.assign(document.createElement('strong'), { textContent: 'may not' }),
                    ' be eligible for this funding:'
                );

                const ineligibleItems = result.ineligible.map(({ target }) => stored.items[target]);
                const tagUl = this.buildTagList(ineligibleItems, { ineligible: true });
                
                const heading = this.createIconListWithContent(
                    CLASS_NAMES.ICON_INELIGIBLE,
                    [p, tagUl]
                );
                fragment.appendChild(heading);
            }

            container.replaceChildren(fragment);
        },

        updateUI() {
            const containers = this.getContainers();
            const matchesByTarget = {};

            containers.forEach(container => {
                const targets = this.getTargets(container);
                if (targets.length === 0) return;

                const result = window.QLD.fundingMatcher.findPersonalFunding(targets);
                this.renderMessage(container, result);

                if (result.eligible && result.eligible.length > 0) {
                    const stored = this._store.get(container.getAttribute('data-fe-id'));
                    result.eligible.forEach(({ target, label }) => {
                        if (!matchesByTarget[target]) {
                            matchesByTarget[target] = {
                                target,
                                label,
                                costWording: this.getCostWording(target, stored)
                            };
                        }
                    });
                }
            });

            this.renderFundingCosts(matchesByTarget);
        },

        init() {
            if (this._initialized) {
                return;
            }

            const containers = this.getContainers();
            const costContainers = this.getCostContainers();

            if ((!containers || containers.length === 0) && (!costContainers || costContainers.length === 0)) {
                return;
            }

            this._initialized = true;

            // Build storage for each container before any DOM changes
            containers.forEach((container, i) => {
                const id = String(i);
                container.setAttribute('data-fe-id', id);

                const items = {};
                container.querySelectorAll('[data-goal-target]').forEach(el => {
                    items[el.getAttribute('data-goal-target')] = el.cloneNode(true);
                });

                const fundingTypeText = container.querySelector('[data-role="funding-type-text"]');

                this._store.set(id, {
                    originalHTML: container.innerHTML,
                    items,
                    fundingTypeText: fundingTypeText ? fundingTypeText.cloneNode(true) : null
                });
            });

            costContainers.forEach((container, i) => {
                const id = String(i);
                container.setAttribute('data-fe-cost-id', id);
                this._costStore.set(id, {
                    originalHTML: container.innerHTML
                });
            });

            window.addEventListener('eligibility:saved', () => {
                this.updateUI();
            });

            window.addEventListener('eligibility:cleared', () => {
                this.updateUI();
            });

            window.addEventListener('eligibility:invalidated', () => {
                this.updateUI();
            });

            // Listen for storage changes from other tabs
            window.addEventListener('storage', (event) => {
                if (window.QLD && window.QLD.eligibilityStorage && event.key === window.QLD.eligibilityStorage.storageKey) {
                    window.QLD.eligibilityStorage.invalidateCache();
                    this.updateUI();
                }
            });

            this.updateUI();
        }
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
            FundingEligibilityUI.init();
        });
    } else {
        FundingEligibilityUI.init();
    }

}());